
import { Component, Vue, Watch } from "vue-property-decorator"
import { Action, Getter, Mutation, State } from "vuex-class"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import PasswordReset from "@/components/Login/PasswordReset.vue"
import LoginMfaSetup from "@/components/Login/LoginMfaSetup.vue"
import MFAEntry from "@/components/Login/MFAEntry.vue"

import {
	CurrentLogin,
	LoginRequest,
	LoginResultTenant,
	LoginState
} from "@/types/loginRequest"

import api from "@/services/api"
import tenantApi from "@/services/api_Tenant"
import { HideableLoginOptions, LoginDomain, SamlUrl } from "@/types/domain"

import ZendeskHelp from "@/components/ZendeskHelp.vue"
import axios from "axios"
import OAuthLogin from "@/components/Login/OAuthLogin.vue"
import Introduction from "@/components/Login/Introduction.vue"

import setBrandingLink from "@/scripts/brandingLink"
import { CurrentUser } from "@/types/user"

export enum LoginSteps {
	Email = 0,
	Password = 1,
	SAML = 2,
}

@Component({
	components: {
		"oauth-login": OAuthLogin,
		scrollbar: VuePerfectScrollbar,
		"password-reset": PasswordReset,
		"mfa-setup": LoginMfaSetup,
		"mfa-entry": MFAEntry,
		"zendeskHelp": ZendeskHelp,
		"introduction": Introduction
	},
	data () {
		return {
			LoginState
		}
	}
})
export default class Login extends Vue {
	@Getter("getMe")
	public me!: CurrentUser;

	@Getter
	public getDarkMode!: boolean;

	@Getter("getScreenWidth")
	public screenWidth!: number;

	@State
	public selectTenants!: LoginResultTenant[] | null;

	@State
	public manualLogout!: boolean;

	@State
	public loginState!: LoginState;

	@State
	public currentLogin!: CurrentLogin | null;

	@Action("selectTenant")
	public loginSelectTenant!: (tenant: LoginResultTenant) => Promise<void>;

	@Action("finishLogin")
	public finishLogin!: () => Promise<void>;

	@Mutation
	public setSelectTenants!: (tenants: LoginResultTenant[] | null) => void;

	@Mutation
	private setManualLogout!: (manualLogout: boolean) => void;

	@Mutation
	private setLoginState!: (loginState: LoginState) => void;

	@Mutation
	private setCurrentLogin!: (currentLogin: CurrentLogin | null) => void;

	private readonly loginSteps = LoginSteps;
	private step: LoginSteps = LoginSteps.Email;

	public email: string = "";
	public password: string = "";
	public loggingIn: boolean = false;
	public error: string | null = "";

	public forgotPassword: boolean = false;

	public resetting: boolean = false;
	public passwordReset: boolean = false;
	public passwordChanged: boolean = false;

	clientIds: { [type: string]: string } = {};

	public oauthLoginError: boolean = false;
	public oauthLoginErrorMessage: string | null = null;

	private selectingTenant: boolean = false;

	private domainLoaded: boolean = false;
	private domain: LoginDomain | null = null;
	private domainName: string = "";
	private domainLogoFailed: boolean = false;

	private logoLoaded: boolean = false;

	/**
	 * Login. If successful, changes route
	 */
	@Action public logIn!: (login: LoginRequest) => Promise<void>;

	@Action public logOut!: () => Promise<void>;

	@Action public userInit!: () => Promise<void>;

	@Action public refreshUserInfo!: () => void;

	get redirectUrl () {
		let redirectUrl = process.env.VUE_APP_HTTP_API_URI || ""
		return redirectUrl.indexOf("://") > 0
			? redirectUrl
			: new URL(redirectUrl, window.location.href).toString()
	}

	get googleOAuthEnabled (): boolean {
		return (
			!!this.clientIds &&
			!!this.clientIds["google"] &&
			this.clientIds["google"] !== ""
		)
	}

	get pageLoaded (): boolean {
		return this.domainLoaded && this.logoLoaded
	}

	/**
	 * Google OAuth Url
	 */
	public get googleOauthUrl (): string {
		if (!this.clientIds || !this.clientIds["google"]) return ""
		const clientId = this.clientIds["google"]
		return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${this.redirectUrl}oauth/login/google&scope=email profile&access_type=online&response_type=code`
	}

	/**
	 * Facebook OAuth Url
	 */
	public get facebookOauthUrl (): string {
		if (!this.clientIds || !this.clientIds["facebook"]) return ""
		const clientId = this.clientIds["facebook"]
		return `https://www.facebook.com/v6.0/dialog/oauth?client_id=${clientId}&redirect_uri=${this.redirectUrl}oauth/login/facebook&scope=email&response_type=code`
	}

	public get samlProviders (): SamlUrl[] | null {
		if (this.domain && this.domain.samlProviders) {
			return this.domain.samlProviders
		}
		return null
	}

	public async mounted () {
		let domainName = location.pathname.replace(/\//g, "")
		if (!domainName || domainName.length === 0) {
			domainName = "default"
		}

		this.domainName = domainName

		this.checkForOauthLoggingError()
		const domain = await tenantApi.getDomain(this.domainName)
		this.domain = domain

		if (this.domain && this.domain.tenantId) {
			setBrandingLink(this.domain.tenantId)
		}

		if (
			!this.oauthLoginErrorMessage &&
			domain &&
			domain.samlProviders &&
			!this.manualLogout
		) {
			const autoLogin = domain.samlProviders.find(
				p => p.autoLogin && !p.error && p.url
			)
			if (autoLogin) {
				window.location.assign(autoLogin.url!)
			}
		}

		this.setManualLogout(false)
		this.domainLoaded = true

		this.clientIds = await api.clientIds()
	}

	/**
	 * Current web version
	 */
	public get webVersion (): string {
		return "1.0.0"
	}

	public get resetCode (): any {
		return this.$route ? this.$route.query.resetCode : null
	}

	private get logoSrc (): string {
		if (this.domainName.length > 0 && !this.domainLogoFailed) {
			return `${process.env.VUE_APP_HTTP_API_URI}tenant/logo/${this.domainName}`
		} else {
			return "images/cases_logo_112020_light.png"
		}
	}

	private get logoAlt () {
		if (this.domainName.length > 0 && !this.domainLogoFailed) {
			return "Logo"
		}
		return "SureView Cases Logo"
	}

	private async created (): Promise<void> {
		if (!this.$route || !this.$route.query) {
			return
		}

		let username = this.$route.query.user as string
		if (username && await api.checkIfUserIsSuite(username)) {
			this.email = username

			await this.refreshUserInfo()
			if (this.me && this.me.email === username) {
				this.$router.push({ path: "dashboard" })
			} else {
				await this.suiteLogin()
			}
		}
	}

	private async next (skipAccountAdminCheck: boolean = false) {
		this.loggingIn = true

		this.error = ""

		try {
			if (this.isUserSuite) {
				this.suiteLogin()
				return
			} else {
				this.step = this.loginSteps.Password
			}
		} catch (ex) {
			if (ex.response.status === 404) {
				this.error = "Couldn't send you to suite to log in."
			} else {
				console.log(`Unexpected error occurred: ${ex.message}. Stack trace - ${ex.stack || ex}`)
				this.error = "Something went wrong, please try again later and contact support if the issue persists"
			}
		}
		this.loggingIn = false
	}

	/**
	 * Do login with username and password
	 */
	public async login () {
		this.error = null
		this.loggingIn = true
		this.passwordChanged = false

		try {
			await this.logIn({ email: this.email, password: this.password })
		} catch (err) {
			this.error = err
		}
		this.loggingIn = false
	}

	/**
	 * Requests password change
	 */
	public async requestResetPassword () {
		this.resetting = true

		try {
			await api.requestPasswordReset({ email: this.email })
			this.passwordReset = true
		} catch (err) {
			console.log(err)
			this.error = err
		}

		this.resetting = false
	}

	/**
	 * Reset password
	 */
	public async resetPassword ({
		newPassword,
		resetComplete
	}: {
		newPassword: string;
		resetComplete: () => void;
	}) {
		try {
			this.error = null
			await api.resetPassword({
				email: this.email,
				newPassword: newPassword,
				resetCode: this.resetCode
			})

			this.$router.replace({ name: "login", query: {} })
		} catch (err) {
			this.error = err as string
		}

		resetComplete()
		this.passwordChanged = true
	}

	/**
	 * Shows/hides the forgot password link.
	 * Is false if the login domain settings have the forgot password option hidden, true otherwise
	 */
	public get canUseForgotPassword (): boolean {
		return (
			!this.isUserSuite &&
			(!this.domain ||
				(!!this.domain.hiddenElements &&
					!this.domain.hiddenElements.includes(
						HideableLoginOptions.ForgotPassword
					)))
		)
	}

	/**
	 * Shows/hides sso.
	 * Is false if the login domain settings have the sso option hidden, true otherwise
	 */
	public get canUseSso (): boolean {
		return (
			!this.domain ||
			(!!this.domain.hiddenElements &&
				!this.domain.hiddenElements.includes(HideableLoginOptions.Sso))
		)
	}

	/**
	 * Moves the sso buttons to under the login button.
	 * Is true if the login domain settings have the sso below login option set, false otherwise
	 */
	public get showSsoBelowLogin (): boolean {
		return !!this.domain && this.domain.showSsoBelowLogin
	}

	/**
	 * Checks if the sign-up process is allowed.
	 * Sign up is allowed if it is defined in the .env files, and it is not disabled in the login domain settings
	 */
	public get canSignUp (): boolean {
		return (
			(process.env.VUE_APP_SIGN_UP_ENABLED &&
				process.env.VUE_APP_SIGN_UP_ENABLED.toLowerCase() === "true" &&
				(!this.domain ||
					(this.domain.hiddenElements &&
						!this.domain.hiddenElements.includes(
							HideableLoginOptions.Signup
						)))) ||
			false
		)
	}

	/**
	 * Shows/Hides the logo.
	 * Is false if the login domain settings have the logo option hidden, true otherwise
	 */
	public get showLogo (): boolean {
		return (
			!this.domain ||
			(this.domain.hiddenElements &&
				!this.domain.hiddenElements.includes(HideableLoginOptions.Logo))
		)
	}

	/**
	 * Go to sign-up page
	 */
	private redirectSignup () {
		this.$router.push({ path: "signup" })
	}

	/**
	 * Check for errors in OAuth
	 */
	public checkForOauthLoggingError () {
		this.oauthLoginError = false
		if (
			this.$route &&
			this.$route.fullPath &&
			this.$route.fullPath.includes("/login?error=")
		) {
			const error = this.$route.query.error

			if (error === "select-tenant") {
				this.selectingTenant = true
				tenantApi
					.selectTenantsList()
					.then(t => {
						this.setSelectTenants(t && t.length > 0 ? t : null)

						if (t && t.length > 0 ? t : null) {
							this.setCurrentLogin({
								allowSystemAdmin: false,
								requiresMfaSetup: false,
								supportAccess: null
							})
							this.setLoginState(LoginState.PendingTenantSelect)
						} else {
							this.setLoginState(LoginState.LoggedIn)
						}

						this.selectingTenant = false
					})
					.catch(err => {
						console.log(err)
						this.selectingTenant = false
					})
			} else if (error === "mfa") {
				this.setLoginState(LoginState.PendingMfa)
			} else {
				this.oauthLoginError = true
				this.oauthLoginErrorMessage = `Login Error: ${error}`
			}
		}
	}

	/**
	 * Return errors in OAuth
	 */
	public get oauthLoginErrorString (): string | null {
		return this.oauthLoginErrorMessage
	}

	private selectTenant (loginTenant: LoginResultTenant) {
		if (loginTenant && !loginTenant.disabled) {
			this.loginSelectTenant(loginTenant)
		}
	}

	private cancelTenantSelect () {
		this.$route.query.error = ""
		this.logOut()
	}

	private logoLoadError () {
		this.domainLogoFailed = true
	}

	private mfaSuccess () {
		if (this.loginState === LoginState.PendingSystemMfa) {
			this.setLoginState(LoginState.LoggedIn)
			this.$router.push({ path: "/system" })
			this.userInit()
		} else {
			this.finishLogin()
		}
	}

	adminLogin () {
		if (!this.currentLogin) return

		if (this.currentLogin.requiresMfaSetup) {
			this.setLoginState(LoginState.PendingSystemMfaSetup)
		} else {
			this.setLoginState(LoginState.PendingSystemMfa)
		}
	}

	mfaSet () {
		if (this.loginState === LoginState.PendingSystemMfaSetup) {
			this.setLoginState(LoginState.LoggedIn)
			this.$router.push({ path: "/system" })
			this.userInit()
		}
	}

	mfaCancel () {
		this.setLoginState(LoginState.NotLoggedIn)
	}

	private checkingEmail: boolean = false;
	private isUserSuite: boolean = false;

	@Watch("email")
	private checkAccountBelongsToSuite () {
		if (this.checkingEmail) {
			return
		}

		this.checkingEmail = true
		setTimeout(async () => {
			try {
				this.isUserSuite = await api.checkIfUserIsSuite(this.email)
				if (this.isUserSuite) {
					this.password = ""
				}
			} catch (ex) {
				console.error(ex)
				this.isUserSuite = false
			}
			this.checkingEmail = false
		}, 500)
	}

	private gotoEmailStep () {
		if (this.step !== this.loginSteps.Email) {
			this.step = this.loginSteps.Email
		}
	}

	private async suiteLogin (): Promise<void> {
		this.loggingIn = true

		this.error = ""
		const genericError =
			"Something went wrong, please try again later and contact support if the issue persists"
		try {
			let axiosInstance = axios.create({
				baseURL: process.env.VUE_APP_HTTP_API_URI,
				withCredentials: true,
				headers: {
					Accept: "application/json, text/javascript, */*; q=0.01",
					"Content-Type": "application/x-www-form-urlencoded"
				}
			})
			let loginProviderResult = await axiosInstance.get(
				`/login/generateSuiteSaml?email=${encodeURIComponent(this.email)}`
			)

			if (loginProviderResult.status === 200) {
				window.location.href = loginProviderResult.data.authorizationEndpoint
			} else {
				this.error = genericError
				this.loggingIn = false
			}
		} catch (ex) {
			console.log(
				`Unexpected error occurred: ${ex.message}. Stack trace - ${ex.stack ||
					ex}`
			)
			this.error = genericError
			this.loggingIn = false
		}
	}

	private get loginDisabled (): boolean {
		if (this.isUserSuite) {
			return this.loggingIn || !this.email
		} else {
			return this.loggingIn || !this.email || !this.password
		}
	}
}
